import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { sanityClient, urlFor } from "../lib/sanityClient";
import "../styles/IntroSection.css";

const QUERY = `*[_type == "introSection"][0]{
  brandHeading, brandLogos, subheading, headingMain,
  headingHighlight, introImage, paragraphs
}`;

const ExpandableText = ({ text, maxLength }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!text) return null;

  // Replace literal \n sequences from Sanity with actual newlines
  const cleanText = text.replace(/\\n/g, '\n');
  const shouldTruncate = cleanText.length > maxLength;
  const displayText = isExpanded || !shouldTruncate ? cleanText : `${cleanText.substring(0, maxLength).trim()}...`;

  return (
    <span style={{ whiteSpace: "pre-line" }}>
      {displayText}
      {shouldTruncate && (
        <span
          onClick={() => setIsExpanded(!isExpanded)}
          style={{
            color: "#4de352",
            cursor: "pointer",
            fontWeight: "600",
            marginLeft: "6px",
            textDecoration: "underline",
            fontSize: "16px",
          }}
        >
          {isExpanded ? "Show Less" : "Read More"}
        </span>
      )}
    </span>
  );
};

const IntroSection = () => {
  const [data, setData] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    console.log("IntroSection: Fetching data from Sanity...");
    sanityClient.fetch(QUERY).then(res => {
      console.log("IntroSection Data:", res);
      setData(res);
    }).catch(err => console.error("IntroSection Fetch Error:", err));
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const logos = (data?.brandLogos || []).map((l) => urlFor(l.logo).url());

  // Animations
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <motion.section
      className="intro-section"
      id="about"
      initial="visible"
      animate="visible"
      variants={containerVariants}
    >
      {data && (
        <>
          {/* Brand Trust */}
          <motion.div className="brand-trust" variants={fadeUp}>
            <p className="brand-heading">{data.brandHeading}</p>

        {isMobile ? (
          <div className="marquee-wrapper">
            <motion.div
              className="marquee-content"
              animate={{ x: ["0%", "-50%"] }}
              transition={{ repeat: Infinity, duration: 15, ease: "linear" }}
            >
              {[...logos, ...logos].map((src, i) => (
                <motion.img
                  src={src}
                  alt="brand"
                  className="marquee-logo"
                  key={i}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.2 }}
                />
              ))}
            </motion.div>
          </div>
        ) : (
          <motion.div className="brand-logos-wrapper" variants={fadeUp}>
            <div className="brand-logos">
              {logos.map((src, i) => (
                <motion.img
                  src={src}
                  alt="brand"
                  key={i}
                  className="brand-logo"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.2, duration: 0.5 }}
                />
              ))}
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* Intro Content */}
      <motion.div className="intro-content" variants={containerVariants}>
        <motion.div
          className="intro-radial-bg"
          initial={{ opacity: 0.5, scale: 0.95 }}
          animate={{ opacity: [0.5, 0.8, 0.5], scale: [0.95, 1.1, 0.95] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />

        {/* Left Column */}
        <motion.div className="intro-left" variants={fadeUp}>
          <p className="intro-subheading">{data.subheading}</p>
          <h2 className="intro-heading">
            {data.headingMain}
            <span className="highlighted-gpt">{data.headingHighlight}</span>
          </h2>
          <motion.div
            className="intro-visual"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            <img src={urlFor(data.introImage).url()} alt="Visual" />
          </motion.div>
        </motion.div>

        <div className="intro-divider"></div>

        {/* Right Column */}
        <motion.div className="intro-right" variants={containerVariants}>
          {(data.paragraphs || []).map((p, index) => (
            <motion.p className="intro-paragraph" variants={fadeUp} key={p._key || index}>
              <ExpandableText text={p.text} maxLength={850} />
            </motion.p>
          ))}
        </motion.div>
      </motion.div>
      </>
      )}
    </motion.section>
  );
};

export default IntroSection;
